import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtNum } from "@/utils/toEnglishDigits";
import { fmtRiyal } from "@/utils/toEnglishDigits";
import { fmtDate, fmtTime, combineDateTime } from "@/utils/date";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import { buildDriverWelcomeUrl } from "@/utils/driverOnboarding";
import PhoneInput from "@/components/PhoneInput";
import MultiPhoneInput, { hydratePhones, serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import { validatePhone } from "@/utils/phoneValidation";
import NumberField from "@/components/NumberField";
import { toEnglishDigits } from "@/utils/toEnglishDigits";
import {
  User as UserIcon,
  WhatsappLogo,
  Plus,
  Pencil,
  Trash,
  MagnifyingGlass,
  Clock,
  CheckCircle,
  XCircle,
  Key,
  ArrowsClockwise,
  Eye,
  PauseCircle,
  PlayCircle,
  Note,
  IdentificationCard,
  Copy,
  Phone,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";

const emptyForm = () => ({
  name: "",
  phones: [{ number: "+966", is_primary: true }],
  whatsapp_phone: "",
  license_plate: "",
  car_type: "",
  access_code: "",
  daily_wage: 0,
  active: true,
  display_order: "",
});

function DriverModal({ open, initial, cars, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || "",
        phones: hydratePhones(initial.phones, initial.phone),
        whatsapp_phone: initial.whatsapp_phone || initial.phone || "",
        license_plate: initial.license_plate || "",
        car_type: initial.car_type || "",
        access_code: "",
        daily_wage: initial.daily_wage || 0,
        active: initial.is_active !== false,
        display_order: initial.display_order ? String(initial.display_order) : "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    const phoneError = validatePhonesList(form.phones);
    if (phoneError) return toast.error(phoneError);
    const phones = serializePhones(form.phones);
    if (phones.length === 0) return toast.error("رقم الجوال الأساسي مطلوب");
    if (form.whatsapp_phone && String(form.whatsapp_phone).replace(/\D/g, "").length > 3) {
      const waCheck = validatePhone(form.whatsapp_phone);
      if (!waCheck.ok) return toast.error(`واتساب: ${waCheck.reason}`);
    }
    if (!isEdit && !form.access_code) return toast.error("رمز الوصول مطلوب (6 أرقام)");
    setSaving(true);
    try {
      const primary = phones.find((p) => p.is_primary)?.number || phones[0].number;
      // Parse display_order (blank = auto-sort). 1-9999 pins the driver.
      const _do = parseInt(String(form.display_order).trim(), 10);
      const orderVal = !Number.isNaN(_do) && _do >= 0 ? _do : undefined;
      const payload = {
        ...form,
        phones,
        phone: primary,
        whatsapp_phone: form.whatsapp_phone || primary,
      };
      delete payload.display_order;
      if (isEdit) {
        // On edit: send 0 to clear pin, positive to set. Always include so admin can un-pin.
        payload.display_order = orderVal === undefined ? 0 : orderVal;
      } else if (orderVal !== undefined && orderVal > 0) {
        payload.display_order = orderVal;
      }
      if (isEdit) {
        if (!payload.access_code) delete payload.access_code;
        await api.put(`/drivers/${initial.id}`, payload);
        toast.success("تم تحديث السائق");
      } else {
        await api.post("/drivers", payload);
        toast.success(`تم إنشاء السائق — رمز الوصول: ${form.access_code}`);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="driver-modal"
    >
      <div
        className="bg-card border border-primary/25 rounded-2xl w-full max-w-lg gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-primary/15 flex items-center justify-between">
          <h3 className="text-primary font-bold text-lg">{isEdit ? "تعديل السائق" : "سائق جديد"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="driver-modal-close">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">الاسم *</div>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mz-input" data-testid="driver-name" />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">أرقام التواصل *</div>
            <MultiPhoneInput
              value={form.phones}
              onChange={(v) => set("phones", v)}
              testIdPrefix="driver-phones"
              required
              maxEntries={5}
            />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right flex items-center gap-1 justify-end">
              <WhatsappLogo size={13} weight="fill" className="text-[#25D366]" /> رقم واتساب (اختياري)
            </div>
            <PhoneInput value={form.whatsapp_phone} onChange={(v) => set("whatsapp_phone", v)} testId="driver-wa-phone" placeholder="مطابق للجوال إذا تركته فارغاً" />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">رقم اللوحة</div>
            <input value={form.license_plate} onChange={(e) => set("license_plate", e.target.value)} className="mz-input" dir="ltr" data-testid="driver-plate" />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">
              <span className="text-primary font-bold">ترتيب العرض</span>
              <span className="text-white/45 text-[11px] font-medium"> (اختياري — الأصغر أولاً، اتركه فارغاً للترتيب التلقائي)</span>
            </div>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={form.display_order}
              onChange={(e) => set("display_order", toEnglishDigits(e.target.value).replace(/[^0-9]/g, ""))}
              placeholder="مثال: 1، 2، 3..."
              className="mz-input num"
              dir="ltr"
              data-testid="driver-order-input"
            />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">نوع السيارة</div>
            <div dir="rtl" className="flex flex-wrap gap-2" data-testid="driver-car-chips">
              {cars.map((c) => {
                const active = form.car_type === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => set("car_type", active ? "" : c.id)}
                    data-testid={`driver-car-chip-${c.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                    }`}
                  >
                    {c.name_ar}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">{isEdit ? "رمز جديد (اختياري)" : "رمز الوصول *"}</div>
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={form.access_code}
                onChange={(e) => set("access_code", toEnglishDigits(e.target.value).replace(/\D/g, ""))}
                className="mz-input tracking-widest text-center num"
                dir="ltr"
                placeholder="000000"
                data-testid="driver-code"
              />
            </label>
            <label className="block">
              <div className="text-xs text-white/60 mb-1 text-right">الأجرة اليومية (اختياري)</div>
              <NumberField min={0} value={form.daily_wage} onChange={(val) => set("daily_wage", val === "" ? "" : Number(val))} className="mz-input num" testId="driver-wage" placeholder="0" />
            </label>
          </div>
          <label className="flex items-center gap-2 mt-1 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-[#C9A961]" data-testid="driver-active" />
            <span className="text-sm text-white/80">نشط</span>
          </label>
          <div className="flex gap-3 pt-3">
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="driver-save">
              {saving ? "جارٍ الحفظ…" : (isEdit ? "حفظ التعديلات" : "إنشاء")}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal that reveals the driver's full access code ─────────────────
function AccessCodeModal({ driver, code, onClose, onResend }) {
  if (!driver || !code) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("تم نسخ الرمز");
    } catch {
      toast.error("تعذر النسخ");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="access-code-modal"
    >
      <div
        className="bg-card border border-primary/25 rounded-2xl w-full max-w-md gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-primary/15 flex items-center justify-between">
          <div>
            <h3 className="text-primary font-bold text-lg">رمز الدخول</h3>
            <div className="text-white/60 text-xs mt-0.5">{driver.name}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white">×</button>
        </div>
        <div className="p-6 space-y-4 text-center">
          <div className="rounded-2xl border-2 border-primary bg-primary/10 py-6 px-4">
            <div className="text-white/60 text-xs mb-2">الرمز الحالي</div>
            <div className="num text-primary text-5xl font-black tracking-[0.5em]" dir="ltr" data-testid="access-code-value">
              {code}
            </div>
          </div>
          <div className="rounded-xl border border-[#F57F17]/40 bg-[#F57F17]/10 p-3 text-xs text-[#F59E0B] flex items-start gap-2 text-right">
            <span>⚠️</span>
            <span>هذا الرمز سرّي — لا تشاركه مع أي شخص غير السائق نفسه، وتجنّب عرضه في مكان مكشوف.</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={copy}
              data-testid="access-code-copy"
              className="flex-1 py-2.5 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 text-sm font-bold inline-flex items-center justify-center gap-1.5"
            >
              <Copy size={14} weight="bold" /> نسخ الرمز
            </button>
            {onResend && (
              <button
                onClick={onResend}
                data-testid="access-code-resend"
                className="flex-1 py-2.5 rounded-xl bg-[#25D366]/15 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366]/25 text-sm font-bold inline-flex items-center justify-center gap-1.5"
              >
                <WhatsappLogo size={14} weight="fill" /> إعادة الإرسال
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Pending driver request card ──────────────────────────────────────
function PendingCard({ d, cars, onApprove, onReject, busyId }) {
  const car = cars.find((c) => c.id === d.car_type);
  const carName = car?.name_ar || "";
  const hasCar = !!car;
  const busy = busyId === d.id;
  const iso = d.created_at ? combineDateTime(d.created_at.slice(0, 10), d.created_at.slice(11, 16)) : null;
  const waHref = (() => {
    const raw = (d.whatsapp_phone || d.phone || "").replace(/[^0-9]/g, "");
    if (!raw) return null;
    const msg = `مرحباً ${d.name || ""} 👋\nتلقيّنا طلب انضمامك كسائق لدى شركة مراسم الزوار.\nسنتواصل معك قريباً بإذن الله.`;
    return `https://wa.me/${raw}?text=${encodeURIComponent(msg)}`;
  })();
  return (
    <div
      data-testid={`pending-driver-${d.id}`}
      className="fadeup bg-card border-2 border-[#F57F17]/40 rounded-2xl p-5 hover:border-[#F57F17]/70 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-full bg-[#F57F17]/10 border border-[#F57F17]/40 flex items-center justify-center flex-shrink-0">
          <UserIcon size={26} weight="duotone" className="text-[#F57F17]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-bold truncate">{d.name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F57F17]/15 border border-[#F57F17]/50 text-[#F57F17] inline-flex items-center gap-1">
              <Clock size={10} weight="fill" /> بانتظار الموافقة
            </span>
          </div>
          <div className="text-white/70 text-sm mt-1.5 num" dir="ltr">
            {formatPhoneDisplay(d.phone)}
          </div>
          {d.whatsapp_phone && d.whatsapp_phone !== d.phone && (
            <div className="text-[#25D366] text-xs mt-1 num inline-flex items-center gap-1" dir="ltr">
              <WhatsappLogo size={11} weight="fill" /> {formatPhoneDisplay(d.whatsapp_phone)}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {hasCar ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-primary/25 text-white/90">
                🚗 {carName}
              </span>
            ) : (
              <span
                data-testid={`pending-no-car-${d.id}`}
                className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#F5A623]/20 border border-[#F5A623] text-[#F5A623]"
              >
                🚗 لم يُعيَّن
              </span>
            )}
            {d.license_plate && (
              <span className="text-xs font-extrabold px-2 py-0.5 rounded-full border border-primary text-primary inline-flex items-center gap-1">
                <IdentificationCard size={11} /> <span className="num" dir="ltr">{d.license_plate}</span>
              </span>
            )}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid={`wa-pending-${d.id}`}
                onClick={(e) => e.stopPropagation()}
                className="ms-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#25D366] hover:bg-[#1FA754] text-white text-xs font-extrabold shrink-0"
              >
                <WhatsappLogo size={13} weight="fill" /> واتساب
              </a>
            )}
          </div>
          {d.notes && (
            <div className="text-white/50 text-xs mt-2 flex items-start gap-1.5 border-t border-white/5 pt-2">
              <Note size={12} className="text-primary/60 mt-0.5 shrink-0" />
              <span className="line-clamp-2">{d.notes}</span>
            </div>
          )}
          {iso && (
            <div className="text-white/40 text-[11px] mt-2 num" dir="ltr">
              {fmtDate(iso)} · {fmtTime(iso)}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-primary/10">
        <button
          onClick={() => onApprove(d)}
          disabled={busy}
          data-testid={`approve-driver-${d.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#2E7D32]/20 border border-[#2E7D32]/60 text-[#22C55E] hover:bg-[#2E7D32]/35 text-sm font-bold transition-all disabled:opacity-60"
        >
          <CheckCircle size={16} weight="bold" /> قبول
        </button>
        <button
          onClick={() => onReject(d)}
          disabled={busy}
          data-testid={`reject-driver-${d.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-destructive/15 border border-destructive/60 text-destructive hover:bg-destructive/25 text-sm font-bold transition-all disabled:opacity-60"
        >
          <XCircle size={16} weight="bold" /> رفض
        </button>
      </div>
    </div>
  );
}

export default function DriversPage() {
  const [drivers, setDrivers] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [carF, setCarF] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [tab, setTab] = useState("active"); // "active" | "pending"
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [codeModal, setCodeModal] = useState(null); // { driver, code }
  // 2026-07 §DriverEarningsBadges: driver_id → { trips, wages, revenue }.
  const [earnings, setEarnings] = useState({});
  const confirm = useConfirm();
  const timerRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [d, c, f] = await Promise.all([
        api.get("/drivers"),
        api.get("/cars"),
        api.get("/finance/summary").catch(() => ({ data: { per_driver: [] } })),
      ]);
      setDrivers(Array.isArray(d.data) ? d.data : []);
      setCars(Array.isArray(c.data) ? c.data : []);
      const map = {};
      const list = (f.data && f.data.per_driver) || [];
      for (const row of list) {
        if (!row.driver_id) continue;
        map[row.driver_id] = {
          trips: Number(row.trips || 0),
          wages: Number(row.wages || 0),
          revenue: Number(row.revenue || 0),
        };
      }
      setEarnings(map);
    } catch {
      if (!silent) toast.error("تعذر تحميل السائقين");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so pending badge stays fresh.
  useEffect(() => {
    timerRef.current = setInterval(() => load({ silent: true }), 30000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const pending = useMemo(
    () => drivers.filter((d) => d.status === "pending_review"),
    [drivers],
  );
  const nonPending = useMemo(
    () => drivers.filter((d) => d.status !== "pending_review"),
    [drivers],
  );

  const filtered = useMemo(() => {
    let list = nonPending;
    if (carF === "__no_car__") {
      const validIds = new Set(cars.map((c) => c.id));
      list = list.filter((d) => !d.car_type || !validIds.has(d.car_type));
    } else if (carF) {
      list = list.filter((d) => d.car_type === carF);
    }
    if (statusF === "active") list = list.filter((d) => d.status === "active" || (d.is_active !== false && !d.status));
    if (statusF === "suspended") list = list.filter((d) => d.status === "suspended" || (d.is_active === false && d.status !== "rejected"));
    if (statusF === "rejected") list = list.filter((d) => d.status === "rejected");
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((d) =>
        (d.name || "").toLowerCase().includes(q) ||
        (d.phone || "").includes(q),
      );
    }
    return list;
  }, [nonPending, cars, carF, statusF, search]);

  const noCarCount = useMemo(() => {
    const validIds = new Set(cars.map((c) => c.id));
    return nonPending.filter((d) => !d.car_type || !validIds.has(d.car_type)).length;
  }, [nonPending, cars]);

  const carName = (id) => cars.find((c) => c.id === id)?.name_ar || "—";

  // ─── Actions ────────────────────────────────────────────────────────
  const approveDriver = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await api.post(`/admin/drivers/${d.id}/approve`);
      const code = data.access_code;
      toast.success(`تم قبول ${d.name} — فتحت واتساب`);
      const url = buildDriverWelcomeUrl({
        name: d.name,
        phone: d.phone,
        whatsappPhone: d.whatsapp_phone || d.phone,
        accessCode: code,
      });
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const rejectDriver = async (d) => {
    const ok = await confirm({
      title: "رفض السائق",
      message: `رفض طلب "${d.name}"؟ لن يتمكن من الدخول ولن يُنشأ له رمز.`,
      confirmText: "نعم، ارفض",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await api.post(`/admin/drivers/${d.id}/reject`);
      toast.success("تم رفض الطلب");
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const viewCode = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await api.get(`/admin/drivers/${d.id}/access-code`);
      setCodeModal({ driver: d, code: data.access_code });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const resendWhatsApp = async (d) => {
    setBusyId(d.id);
    try {
      const { data } = await api.get(`/admin/drivers/${d.id}/access-code`);
      const url = buildDriverWelcomeUrl({
        name: d.name,
        phone: d.phone,
        whatsappPhone: d.whatsapp_phone || d.phone,
        accessCode: data.access_code,
      });
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("فتحت واتساب مع الرسالة");
      } else {
        toast.error("رقم واتساب غير صالح");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const regenerateCode = async (d) => {
    const ok = await confirm({
      title: "تجديد الرمز",
      message: `سيتم إبطال الرمز القديم لـ "${d.name}" وتوليد رمز جديد. سيُطلب من السائق استخدام الرمز الجديد فقط.`,
      confirmText: "نعم، جدّد الرمز",
      tone: "warning",
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      const { data } = await api.post(`/admin/drivers/${d.id}/regenerate-code`);
      setCodeModal({ driver: d, code: data.access_code });
      toast.success("تم توليد رمز جديد");
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const suspend = async (d) => {
    const ok = await confirm({
      title: "تعليق السائق",
      message: `سيتم تعليق حساب "${d.name}" ولن يتمكن من الدخول حتى تُعيد تفعيله.`,
      confirmText: "نعم، علّق",
      tone: "warning",
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await api.post(`/admin/drivers/${d.id}/suspend`);
      toast.success("تم تعليق الحساب");
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const activate = async (d) => {
    setBusyId(d.id);
    try {
      await api.post(`/admin/drivers/${d.id}/activate`);
      toast.success("تم إعادة تفعيل الحساب");
      await load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({
      title: "حذف السائق",
      message: `سيتم حذف السائق "${d.name}" بشكل نهائي. لا يمكن التراجع عن هذا الإجراء.`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/drivers/${d.id}`);
      toast.success("تم الحذف");
      load({ silent: true });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const waSimpleLink = (d) => {
    const phone = String(d.whatsapp_phone || d.phone || "").replace(/\D/g, "");
    if (!phone) return null;
    return `https://wa.me/${phone}?text=${encodeURIComponent(`السلام عليكم ${d.name || ""}\nنتواصل معك من مراسم الزوار.`)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-white">السائقون</h1>
              {pending.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTab("pending")}
                  data-testid="header-pending-badge"
                  title="طلبات جديدة بانتظار المراجعة"
                  className="inline-flex flex-row-reverse items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E53935] border border-[#E53935] text-white text-xs font-extrabold min-w-[28px] shadow-[0_0_8px_rgba(229,57,53,0.5)] pending-pulse cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-white" />
                  <span className="num">{fmtNum(pending.length)}</span>
                </button>
              )}
            </div>
            <p className="text-white/50 text-sm mt-1">
              <span className="num">{fmtNum(nonPending.length)}</span> إجمالي ·{" "}
              <span className="num text-[#F57F17]">{fmtNum(pending.length)}</span> بانتظار الموافقة
            </p>
          </div>
          <button
            onClick={() => { setEditing(null); setModalOpen(true); }}
            data-testid="add-driver"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold transition-all"
          >
            <Plus size={18} weight="bold" /> سائق جديد
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 border-b border-primary/15" data-testid="drivers-tabs">
          <button
            onClick={() => setTab("active")}
            data-testid="tab-active"
            className={`relative px-5 py-3 text-sm font-bold transition-all border-b-2 -mb-[1px] ${
              tab === "active" ? "text-primary border-primary" : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            السائقون
            <span className={`num mr-1.5 text-[11px] px-2 py-0.5 rounded-full ${tab === "active" ? "bg-primary/25 text-primary" : "bg-white/10 text-white/60"}`}>
              {fmtNum(nonPending.length)}
            </span>
          </button>
          <button
            onClick={() => setTab("pending")}
            data-testid="tab-pending"
            className={`relative px-5 py-3 text-sm font-bold transition-all border-b-2 -mb-[1px] inline-flex items-center gap-1.5 ${
              tab === "pending" ? "text-[#F57F17] border-[#F57F17]" : "text-white/50 border-transparent hover:text-white/80"
            }`}
          >
            طلبات الانتظار
            {pending.length > 0 && (
              <span
                data-testid="pending-badge"
                className="num text-[11px] px-2 py-0.5 rounded-full bg-destructive text-white font-black animate-pulse"
              >
                {fmtNum(pending.length)}
              </span>
            )}
          </button>
        </div>

        {/* ─── PENDING TAB ───────────────────────────────────────── */}
        {tab === "pending" && (
          <div data-testid="pending-panel">
            {loading ? (
              <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
            ) : pending.length === 0 ? (
              <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">
                لا توجد طلبات معلّقة حالياً ✓
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pending.map((d) => (
                  <PendingCard
                    key={d.id}
                    d={d}
                    cars={cars}
                    onApprove={approveDriver}
                    onReject={rejectDriver}
                    busyId={busyId}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── ACTIVE TAB ─────────────────────────────────────────── */}
        {tab === "active" && (
          <div data-testid="active-panel">
            {/* Filters */}
            <div className="mb-4">
              <div className="relative max-w-xl mb-3">
                <MagnifyingGlass size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="بحث بالاسم أو الجوال…"
                  className="mz-input pr-9"
                  data-testid="drivers-search"
                />
              </div>

              <div className="mb-3">
                <div className="text-primary font-bold text-sm mb-2">🚗 السيارة:</div>
                <div dir="rtl" className="flex flex-row items-center flex-wrap gap-2" data-testid="drivers-car-chips">
                  {[{ id: "", name_ar: "الكل" }, ...cars].map((c) => {
                    const active = carF === c.id;
                    const count = c.id
                      ? nonPending.filter((d) => d.car_type === c.id).length
                      : nonPending.length;
                    return (
                      <button
                        key={c.id || "all"}
                        type="button"
                        onClick={() => setCarF(c.id)}
                        data-testid={`chip-car-${c.id || "all"}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                          active
                            ? "bg-primary text-primary-foreground border-primary font-bold"
                            : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                        }`}
                      >
                        <span>{c.name_ar}</span>
                        <span
                          className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                            active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                          }`}
                        >
                          {fmtNum(count)}
                        </span>
                      </button>
                    );
                  })}
                  {noCarCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setCarF(carF === "__no_car__" ? "" : "__no_car__")}
                      data-testid="chip-car-no-car"
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                        carF === "__no_car__"
                          ? "bg-[#F5A623] text-white border-[#F5A623] font-bold"
                          : "bg-[#F5A623]/15 border-[#F5A623]/60 text-[#F5A623] hover:bg-[#F5A623]/25 font-semibold"
                      }`}
                      title="سائقون بدون نوع سيارة معيَّن"
                    >
                      <span>🚗 بدون سيارة</span>
                      <span
                        className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          carF === "__no_car__" ? "bg-white/25 text-white" : "bg-[#F5A623]/15 border border-[#F5A623]/40 text-[#F5A623]"
                        }`}
                      >
                        {fmtNum(noCarCount)}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <div className="text-primary font-bold text-sm mb-2">🏷️ الحالة:</div>
                <div dir="rtl" className="flex flex-row items-center flex-wrap gap-2" data-testid="drivers-status-chips">
                  {[
                    { key: "all", label: "الكل" },
                    { key: "active", label: "نشط" },
                    { key: "suspended", label: "معلّق" },
                    { key: "rejected", label: "مرفوض" },
                  ].map((s) => {
                    const active = statusF === s.key;
                    const count = (() => {
                      if (s.key === "all") return nonPending.length;
                      if (s.key === "active") return nonPending.filter((d) => d.status === "active" || (d.is_active !== false && !d.status)).length;
                      if (s.key === "suspended") return nonPending.filter((d) => d.status === "suspended" || (d.is_active === false && d.status !== "rejected")).length;
                      return nonPending.filter((d) => d.status === "rejected").length;
                    })();
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setStatusF(s.key)}
                        data-testid={`chip-status-${s.key}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm border transition-all whitespace-nowrap ${
                          active
                            ? "bg-primary text-primary-foreground border-primary font-bold"
                            : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15 font-semibold"
                        }`}
                      >
                        <span>{s.label}</span>
                        <span
                          className={`num text-[11px] font-bold px-2 py-0.5 rounded-full ${
                            active ? "bg-[#0A1936]/25 text-primary-foreground" : "bg-white/10 text-white"
                          }`}
                        >
                          {fmtNum(count)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">لا يوجد سائقون</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((d, i) => {
                  const isSuspended = d.status === "suspended" || d.is_active === false;
                  const isRejected = d.status === "rejected";
                  return (
                    <div
                      key={d.id}
                      data-testid={`driver-${d.id}`}
                      className={`fadeup relative bg-card border rounded-2xl p-5 transition-all ${
                        isRejected
                          ? "border-destructive/30 opacity-70"
                          : isSuspended
                            ? "border-white/15"
                            : "border-primary/15 hover:border-primary/40"
                      }`}
                      style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
                    >
                      {d.display_order ? (
                        <div
                          data-testid={`driver-order-${d.id}`}
                          className="absolute top-2 left-2 min-w-[26px] h-[26px] px-1.5 rounded-full bg-primary text-primary-foreground num text-xs font-extrabold flex items-center justify-center border border-[#0A1628] shadow-[0_2px_6px_rgba(201,169,97,0.3)] z-10"
                          title={`ترتيب العرض: ${d.display_order}`}
                        >
                          {d.display_order}
                        </div>
                      ) : null}
                      <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                          <UserIcon size={26} weight="duotone" className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-bold truncate">{d.name}</span>
                            {isRejected ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/15 border border-destructive/50 text-destructive">مرفوض</span>
                            ) : isSuspended ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 border border-white/25 text-white/70">معلّق</span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#2E7D32]/15 border border-[#2E7D32]/50 text-[#2E7D32]">نشط</span>
                            )}
                          </div>
                          <div className="mt-1.5 space-y-1">
                            {(d.phones && d.phones.length > 0
                              ? d.phones
                              : d.phone ? [{ number: d.phone, is_primary: true }] : []
                            ).map((p, pIdx) => {
                              const digits = String(p.number || "").replace(/\D+/g, "");
                              if (!digits) return null;
                              return (
                                <div key={pIdx} className="flex items-center gap-2 flex-wrap" dir="ltr">
                                  <span className="text-white/80 text-sm num">
                                    {formatPhoneDisplay(p.number)}
                                  </span>
                                  {p.is_primary && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary font-bold">
                                      ★
                                    </span>
                                  )}
                                  <a
                                    href={`tel:${p.number}`}
                                    title="اتصال"
                                    data-testid={`driver-phone-call-${d.id}-${pIdx}`}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 hover:bg-primary/25 border border-primary/40 text-primary"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Phone size={12} weight="fill" />
                                  </a>
                                  <a
                                    href={`https://wa.me/${digits}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="واتساب"
                                    data-testid={`driver-phone-wa-${d.id}-${pIdx}`}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#20BA5A]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <WhatsappLogo size={12} weight="fill" className="text-white" />
                                  </a>
                                </div>
                              );
                            })}
                          </div>
                          <div className="text-white/50 text-xs mt-1">{carName(d.car_type)}</div>
                          {d.has_access_code && (
                            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-primary/80 num">
                              <Key size={11} weight="fill" />
                              <span dir="ltr">****{d.access_code_last2 || "00"}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 mt-4 pt-4 border-t border-primary/10 flex-wrap">
                        {/* 2026-07 §DriverEarningsBadges */}
                        {earnings[d.id] && earnings[d.id].trips > 0 && (
                          <div
                            className="w-full flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-primary/10"
                            data-testid={`driver-earnings-${d.id}`}
                          >
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/15 border border-primary/40 text-primary text-[11px] font-bold num">
                              <CurrencyCircleDollar size={11} weight="fill" />
                              {fmtRiyal(earnings[d.id].wages)}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/20 text-white/80 text-[11px] font-bold num">
                              🎯 {fmtNum(earnings[d.id].trips)} رحلة
                            </span>
                          </div>
                        )}
                        {!isRejected && d.has_access_code && (
                          <>
                            <button
                              onClick={() => viewCode(d)}
                              disabled={busyId === d.id}
                              data-testid={`view-code-${d.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-[11px] font-bold disabled:opacity-60"
                              title="عرض الرمز"
                            >
                              <Eye size={12} weight="bold" /> الرمز
                            </button>
                            <button
                              onClick={() => resendWhatsApp(d)}
                              disabled={busyId === d.id}
                              data-testid={`resend-wa-${d.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 text-[11px] font-bold disabled:opacity-60"
                              title="إعادة إرسال واتساب"
                            >
                              <WhatsappLogo size={12} weight="fill" /> إرسال
                            </button>
                          </>
                        )}
                        {!isRejected && (
                          <button
                            onClick={() => regenerateCode(d)}
                            disabled={busyId === d.id}
                            data-testid={`regen-code-${d.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#F57F17]/50 text-[#F57F17] hover:bg-[#F57F17]/10 text-[11px] font-bold disabled:opacity-60"
                            title="تجديد الرمز"
                          >
                            <ArrowsClockwise size={12} weight="bold" /> تجديد
                          </button>
                        )}
                        {!isRejected && (
                          isSuspended ? (
                            <button
                              onClick={() => activate(d)}
                              disabled={busyId === d.id}
                              data-testid={`activate-${d.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#2E7D32]/50 text-[#2E7D32] hover:bg-[#2E7D32]/10 text-[11px] font-bold disabled:opacity-60"
                              title="تفعيل"
                            >
                              <PlayCircle size={12} weight="bold" /> تفعيل
                            </button>
                          ) : (
                            <button
                              onClick={() => suspend(d)}
                              disabled={busyId === d.id}
                              data-testid={`suspend-${d.id}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-white/20 text-white/70 hover:bg-white/5 text-[11px] font-bold disabled:opacity-60"
                              title="تعليق"
                            >
                              <PauseCircle size={12} weight="bold" /> تعليق
                            </button>
                          )
                        )}
                        {waSimpleLink(d) && (
                          <a
                            href={waSimpleLink(d)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`wa-driver-${d.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#2E7D32]/15 border border-[#2E7D32]/50 text-[#2E7D32] hover:bg-[#2E7D32]/30 text-[11px] font-bold"
                            title="محادثة عامة"
                          >
                            <WhatsappLogo size={12} weight="fill" />
                          </a>
                        )}
                        <button
                          onClick={() => { setEditing(d); setModalOpen(true); }}
                          data-testid={`edit-driver-${d.id}`}
                          className="p-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 mr-auto"
                          title="تعديل"
                        >
                          <Pencil size={12} weight="bold" />
                        </button>
                        <button
                          onClick={() => remove(d)}
                          data-testid={`delete-driver-${d.id}`}
                          className="p-1.5 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
                          title="حذف"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>

      <DriverModal
        open={modalOpen}
        initial={editing}
        cars={cars}
        onClose={() => setModalOpen(false)}
        onSaved={() => load({ silent: true })}
      />
      {codeModal && (
        <AccessCodeModal
          driver={codeModal.driver}
          code={codeModal.code}
          onClose={() => setCodeModal(null)}
          onResend={() => {
            const d = codeModal.driver;
            const url = buildDriverWelcomeUrl({
              name: d.name,
              phone: d.phone,
              whatsappPhone: d.whatsapp_phone || d.phone,
              accessCode: codeModal.code,
            });
            if (url) {
              window.open(url, "_blank", "noopener,noreferrer");
              toast.success("فتحت واتساب مع الرسالة");
            }
          }}
        />
      )}
    </div>
  );
}
